<template>
    <dialog
        ref="dialogEl"
        class="fade-dialog-enter-exit overflow-hidden bg-transparent"
        @cancel.prevent="closeDialog('dialog-cancel-event')"
        @click.self="closeDialog('backdrop')"
        @close="closeDialog('dialog-close-event')"
    >
        <div class="dialog-content flex flex-1 flex-col rounded-3xl bg-white">
            <header class="flex flex-row items-start justify-between gap-3 p-3 md:px-6">
                <h2 class="min-w-0 self-center break-words text-heading-sm text-content-primary">{{ title }}</h2>
                <DsIconButton
                    v-if="showCloseButton"
                    data-testid="dialog-close-button"
                    icon="fa-solid fa-xmark"
                    size="sm"
                    tooltipText=""
                    @click="closeDialog('icon-button')"
                />
            </header>
            <div
                ref="contentRef"
                class="dialog-slotted-content flex-1 overflow-y-auto px-4 md:px-6"
                :class="{
                    'scrolled-content': isContentScrolled,
                }"
                @scroll="refreshScrollState"
            >
                <slot />
            </div>
            <footer class="flex flex-col-reverse justify-end gap-3 p-4 md:flex-row md:px-6 md:pb-4">
                <DsButton
                    v-if="secondaryAction"
                    size="md"
                    variant="secondary"
                    @click="$emit('clickSecondary')"
                >
                    {{ secondaryAction }}
                </DsButton>
                <DsButton
                    :destructive="isPrimaryDestructive"
                    :leading="primaryActionLeading"
                    :loading="primaryLoading"
                    size="md"
                    variant="primary"
                    @click="$emit('clickPrimary')"
                >
                    {{ primaryAction }}
                </DsButton>
            </footer>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import DsIconButton from '@/components/design-system/DsIconButton.vue';
import type { DsButtonProps } from '@ds/form/input/DsButton.vue';
import DsButton from '@ds/form/input/DsButton.vue';
import { ref, useTemplateRef, watch } from 'vue';

export type DsDialogCloseTrigger = 'icon-button' | 'backdrop' | 'dialog-close-event' | 'dialog-cancel-event';

interface DsDialogProps {
    title: string;
    primaryAction: string;
    isPrimaryDestructive?: DsButtonProps['destructive'];
    primaryActionLeading?: string;
    secondaryAction?: string;
    primaryLoading?: boolean;
    showCloseButton?: boolean;
}

export interface DsDialogEmits {
    clickPrimary: [];
    clickSecondary: [];
    close: [trigger: DsDialogCloseTrigger];
    closeAnimationEnd: [trigger: DsDialogCloseTrigger];
}

withDefaults(defineProps<DsDialogProps>(), {
    showCloseButton: true,
});

const emit = defineEmits<DsDialogEmits>();

const open = defineModel<HTMLDialogElement['open']>('open');
const closeTrigger = ref<DsDialogCloseTrigger>('dialog-close-event');
const dialogEl = useTemplateRef('dialogEl');
const contentRef = useTemplateRef('contentRef');
const isContentScrolled = ref(false);

const ANIMATION_DURATION_MS = 150;

const closeDialog = (trigger: DsDialogCloseTrigger) => {
    closeTrigger.value = trigger;
    open.value = false;
};

watch(open, isOpen => {
    if (isOpen) {
        dialogEl.value?.showModal();
        closeTrigger.value = 'dialog-close-event';
        contentRef.value?.scrollTo({ top: 0, behavior: 'instant' });
    } else {
        dialogEl.value?.close(closeTrigger.value);
        emit('close', closeTrigger.value);
        setTimeout(emit, ANIMATION_DURATION_MS, 'closeAnimationEnd', closeTrigger.value); // browser's native `animationend` event not triggered because <dialog> is out of DOM after `.close()`
    }
});

const refreshScrollState = () => {
    if (!contentRef.value) return;

    isContentScrolled.value = contentRef.value.scrollTop > 0;
};
</script>

<style scoped lang="scss">
@import '$sass/dialog-fading.scss';
@import '$sass/breakpoints.scss';

dialog {
    --duration: v-bind('ANIMATION_DURATION_MS + "ms"');

    &::backdrop {
        background-color: rgba(0, 0, 0, 0.7);
    }
    &:open {
        // Caution: only change display property when open to avoid a ghost dialog when closed
        @apply flex;
    }

    .dialog-content {
        $grid-gutter: 24px;
        $total-vertical-margin: theme('spacing.32');

        width: calc(100vw - #{$grid-gutter * 2});
        max-height: calc(100vh - $total-vertical-margin);

        @media screen and (min-width: $DS_BREAKPOINT_MD) {
            width: 560px;
        }
    }

    .dialog-slotted-content.scrolled-content {
        @apply divide-semantic-primary-default border-y;
    }
}
</style>
